import {
  LitElement,
  html,
  css,
  paths,
  classMap,
  unsafeHTML,
  t,
} from '@hh/global.js';

class HHZoomGallery extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    hasOverflow: {
      type: Boolean,
    },
    overflowDirection: {
      type: String,
    },
    disableNext: {
      type: Boolean,
    },
    disablePrev: {
      type: Boolean,
    },
    imagesPreloaded: {
      type: Boolean,
    },
  }

  constructor() {
    super()

    this.hasOverflow = false

    this.classes = {
      active: 'active',
    }
  }

  get featuredSlot() {
    const slot = this.shadowRoot?.querySelector(`slot[name='featured']`)
    return slot?.assignedNodes()?.[0] ?? null
  }

  get featuredImage() {
    const image = this.featuredSlot?.querySelector('img')
    return image ?? null
  }

  set featuredImage(src) {
    const image = this.featuredImage
    if (!image) return

    image.srcset = src
    image.src = src
  }

  get thumbnailContainer() {
    const slot = this.shadowRoot?.querySelector(`slot[name='thumbnails']`)
    return slot?.assignedNodes()?.[0] ?? null
  }

  get thumbnails() {
    const slot = this.shadowRoot?.querySelector(`slot[name='thumbnails']`)
    const thumbnails = slot
      ?.assignedNodes()?.[0]
      ?.querySelectorAll('[data-image]')
    return thumbnails ?? []
  }

  connectedCallback() {
    super.connectedCallback()

    window.addEventListener('zoom:open', this.onZoomOpen.bind(this))
    window.addEventListener('modal:closed', this.onModalClosed.bind(this))
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.checkOverflow()
        this.checkScrollPosition()
      }, 1000)
    })
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    window.removeEventListener('zoom:open', this.onZoomOpen)
    window.removeEventListener('modal:closed', this.onModalClosed)
  }

  firstUpdated() {
    this.thumbnailContainer?.addEventListener(
      'scroll',
      this.checkScrollPosition.bind(this)
    )
  }

  onZoomOpen(e) {
    this.open = true
    this.checkOverflow()
    this.checkScrollPosition()
    this.preloadImages()
    document.documentElement.classList.add('overflow-hidden')

    if (!e?.detail?.element) {
      this.thumbnailContainer?.scrollTo({
        top: 0,
        left: 0,
      })
      this.thumbnails[0]?.click()
    }
  }

  preloadImages() {
    if (this.imagesPreloaded) return
    const images = [...this.thumbnails].map((imageWrapper) => {
      const { image } = imageWrapper.dataset
      return image
    })

    images.forEach((image) => {
      const img = new Image()
      img.src = image
    })
    this.imagesPreloaded = true
  }

  onThumbnailClick(e) {
    const { target } = e
    const imageWrapper = target.closest('[data-image]')
    if (!imageWrapper) return
    this.setActiveThumbnail(imageWrapper)

    const { image } = imageWrapper.dataset

    this.featuredImage = image
  }

  setActiveThumbnail(imageWrapper) {
    this.thumbnails.forEach((imageWrapper) =>
      imageWrapper.classList.remove(this.classes.active)
    )
    imageWrapper.classList.add(this.classes.active)
  }

  checkOverflow() {
    const { offsetHeight, offsetWidth, scrollWidth, scrollHeight } =
      this.thumbnailContainer

    if (scrollHeight > offsetHeight) {
      this.hasOverflow = true
      this.overflowDirection = 'vertical'
    } else if (scrollWidth > offsetWidth) {
      this.hasOverflow = true
      this.overflowDirection = 'horizontal'
    } else {
      this.hasOverflow = false
      this.overflowDirection = null
    }
  }

  checkScrollPosition() {
    if (this.overflowDirection === 'horizontal') {
      const { offsetWidth, scrollLeft, scrollWidth } = this.thumbnailContainer

      const scrollLeftEnd = scrollWidth - offsetWidth
      const scrollRightEnd = 0

      this.disablePrev = scrollLeft === scrollRightEnd
      this.disableNext = scrollLeft >= scrollLeftEnd
    } else if (this.overflowDirection === 'vertical') {
      const { offsetHeight, scrollTop, scrollHeight } = this.thumbnailContainer

      const scrollTopEnd = scrollHeight - offsetHeight - 5
      const scrollBottomEnd = 0

      this.disablePrev = scrollTop === scrollBottomEnd
      this.disableNext = scrollTop >= scrollTopEnd
    }
  }

  onPaginateClick(direction) {
    let newScrollLeft = 0
    let newScrollTop = 0
    if (this.overflowDirection === 'horizontal') {
      const { offsetWidth, scrollLeft } = this.thumbnailContainer
      const scrollAmount = offsetWidth

      const scrollDirection = direction === 'prev' ? -1 : 1
      newScrollLeft = scrollLeft + scrollAmount * scrollDirection
    } else if (this.overflowDirection === 'vertical') {
      const { offsetHeight, scrollTop } = this.thumbnailContainer
      const scrollAmount = offsetHeight

      const scrollDirection = direction === 'prev' ? -1 : 1
      newScrollTop = scrollTop + scrollAmount * scrollDirection
    }

    this.thumbnailContainer.scrollTo({
      top: newScrollTop,
      left: newScrollLeft,
      behavior: 'smooth',
    })
  }

  onModalClosed(e) {
    if (e.target.getAttribute('ctx') !== 'product') return
    this.open = false
  }

  render() {
    return html`
      <div class="container">
        <div class="paginate-wrapper">
          <button
            class=${classMap({
              paginate: true,
              prev: true,
              hide: !this.hasOverflow,
            })}
            ?disabled=${this.disablePrev}
            aria-label="${t('accessibility.previous_slide')}"
            @click=${() => this.onPaginateClick('prev')}
          >
            ${unsafeHTML(paths.chevron)}
          </button>
          <slot
            name="thumbnails"
            @click=${this.onThumbnailClick}
            @scrollend=${this.checkScrollPosition}
          ></slot>
          <button
            class=${classMap({
              paginate: true,
              next: true,
              hide: !this.hasOverflow,
            })}
            ?disabled=${this.disableNext}
            aria-label="${t('accessibility.next_slide')}"
            @click=${() => this.onPaginateClick('next')}
          >
            ${unsafeHTML(paths.chevron)}
          </button>
        </div>
        <div
          class=${classMap({
            'featured-wrapper': true,
            active: this.open,
          })}
        >
          <slot name="featured"></slot>
        </div>
        <slot name="close"></slot>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .container {
      max-width: 102.4rem;
      margin: 0 auto;
      display: flex;
      flex-direction: column-reverse;
      align-items: center;
      position: relative;
      overflow-x: hidden;
    }

    @media (min-width: 1024px) {
      .container {
        flex-direction: row;
      }
    }

    .featured-wrapper {
      width: 100%;
      height: 100%;
      opacity: 0;
      transition: opacity var(--hh-duration-medium) ease;
      transition-delay: var(--hh-duration-medium);
    }

    .featured-wrapper.active {
      opacity: 1;
    }

    .paginate-wrapper {
      display: flex;
      gap: var(--hh-spacing-sm);
      width: 90%;
    }

    @media (min-width: 1024px) {
      .paginate-wrapper {
        display: grid;
        grid-template-areas: 'prev next' 'thumbnails thumbnails';
        width: unset;
      }
    }

    ::slotted([slot='thumbnails']) {
      grid-area: thumbnails;
    }

    .paginate {
      cursor: pointer;
      border: none;
      background-color: rgba(var(--hh-rgb-brand-secondary), 0.9);
      border-radius: 50%;
      padding: 0;
      justify-self: center;
      align-self: center;
      line-height: 0;
    }

    .paginate:hover:not(:disabled) {
      background-color: rgba(var(--hh-rgb-brand-secondary), 1);
    }

    .paginate:disabled {
      opacity: 0.3;
      cursor: not-allowed;
    }

    .paginate.hide {
      display: none;
    }

    @media (min-width: 1024px) {
      .paginate.prev {
        justify-self: flex-end;
      }

      .paginate.next {
        justify-self: flex-start;
      }
    }

    .paginate .icon {
      width: 2.4rem;
      height: 2.4rem;
      display: flex;
      align-items: center;
      color: var(--hh-color-day);
    }

    .paginate.prev .icon {
      grid-area: prev;
      rotate: 90deg;
      translate: -2px;
    }

    .paginate.next .icon {
      grid-area: next;
      rotate: -90deg;
      translate: 2px;
    }

    @media (min-width: 1024px) {
      .paginate.prev .icon {
        rotate: 180deg;
        translate: 0 -1px;
      }
      .paginate.next .icon {
        rotate: 0deg;
        translate: 0 1px;
      }
    }
  `
}

customElements.define('hh-zoom-gallery', HHZoomGallery)
